import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";

export interface StatProps extends Omit<ComponentPropsWithRef<"div">, "children"> {
  label: ReactNode;
  value: ReactNode;
  /** Variation en pourcentage. Le signe pilote la couleur et la flèche. */
  delta?: number;
  /** Pour une métrique où la baisse est une bonne nouvelle (taux de rebond, coût…). */
  invertDelta?: boolean;
  hint?: ReactNode;
  icon?: ReactNode;
}

/** Indicateur chiffré : une métrique, sa valeur, éventuellement sa variation. */
export function Stat({
  className,
  label,
  value,
  delta,
  invertDelta = false,
  hint,
  icon,
  ...props
}: StatProps) {
  const positive = delta !== undefined && delta >= 0;
  const good = invertDelta ? !positive : positive;

  return (
    <div className={cn("flex flex-col gap-1", className)} {...props}>
      <div className="flex items-center gap-2 text-sm text-fg-muted">
        {icon && <span className="[&_svg]:size-4">{icon}</span>}
        {label}
      </div>

      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight text-fg tabular-nums">{value}</span>

        {delta !== undefined && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
              good ? "text-success-fg" : "text-danger-fg",
            )}
          >
            {/* La flèche double la couleur : lue seule, elle porte déjà le sens. */}
            <span aria-hidden="true">{positive ? "↑" : "↓"}</span>
            {Math.abs(delta)}%
            <span className="sr-only">{positive ? " en hausse" : " en baisse"}</span>
          </span>
        )}
      </div>

      {hint && <p className="text-xs text-fg-muted">{hint}</p>}
    </div>
  );
}
